/**
 * Canonical page-id normalization for client-side event dispatch.
 *
 * The frontend keeps two id systems coexisting during the page-object
 * migration: canonical `pg_*` (lives on `object.id`, fed into PageRenderer's
 * `liveKey`) and legacy html_viewer module id (lives on
 * `attributes.legacyModuleId`, also surfaced as `activePageContext.rawId`,
 * also used as the key in `useChat.activeHtmlModules` because the API
 * creates the html_viewer row under that id).
 *
 * Every CustomEvent we dispatch carries a `moduleId` field. PageRenderer's
 * section-update listener accepts the canonical `pg_*` pageId AND
 * `legacyModuleId` as a transitional fallback during the page-object
 * migration window (originally strict canonical-only per #1919, loosened
 * back to dual-id in #2764 follow-up after composite-prefix leaks caused
 * silent edit drops — see the listener at PageRenderer.tsx for the live
 * accept set and the sunset comment). Every dispatch site MUST still
 * pass an id through `toCanonicalPageId` first — the listener's fallback
 * is defense-in-depth, not a substitute for canonicalisation.
 */
export interface CanonicalPageIdContext {
  pageId?: string
  rawId?: string
}

// PagesContainerModule attaches `o:` / `m:` composite prefixes to
// `currentPage.id` to disambiguate object-sourced vs module-sourced entries
// in its merged list. The fragment after the prefix is the actual id
// (canonical `pg_*` for object-sourced; raw uuid for module-sourced).
function stripCompositePrefix(id: string): string {
  return id.startsWith('o:') || id.startsWith('m:') ? id.slice(2) : id
}

export function toCanonicalPageId(
  input: string | undefined | null,
  ctx?: CanonicalPageIdContext | null,
): string | undefined {
  if (!input) return undefined
  const strippedInput = stripCompositePrefix(input)
  if (strippedInput.startsWith('pg_')) return strippedInput

  if (ctx?.pageId) {
    const strippedCtx = stripCompositePrefix(ctx.pageId)
    if (strippedCtx.startsWith('pg_')) {
      if (ctx.rawId && ctx.rawId === strippedInput) return strippedCtx
      if (!ctx.rawId) return strippedCtx
    }
  }

  // Legacy id with no canonical context — best-effort prefix. The server's
  // page-object writer mirrors legacyModuleId with `pg_` prefix, so this
  // is a reasonable last resort.
  return `pg_${strippedInput}`
}

/**
 * Reduce any member of a page-id family to the shared raw html_viewer
 * module id: strips the `o:` / `m:` composite prefix, then the `pg_`
 * canonical prefix. The server mirrors every html_viewer module into a
 * page object whose id is `pg_<moduleId>`, so `raw`, `pg_<raw>`,
 * `o:pg_<raw>` and `m:<raw>` all denote the same page.
 */
export function rawPageId(id: string): string {
  const stripped = stripCompositePrefix(id)
  return stripped.startsWith('pg_') ? stripped.slice(3) : stripped
}

/**
 * True when two ids — in ANY family form — denote the same page. Used by
 * the live-streaming surfaces (PagesContainerModule's event→entry matcher,
 * PageRenderer's listener gates) where the dispatch id and the locally-held
 * id routinely arrive in different forms mid-build: the stream dispatches
 * the canonical `pg_*` id while the live-state map and module rows key on
 * the raw module id. A strict `===` there silently drops chunks and strands
 * the build skeleton. (#pages-streaming-regression)
 */
export function pageIdsEqual(
  a: string | undefined | null,
  b: string | undefined | null,
): boolean {
  if (!a || !b) return false
  return rawPageId(a) === rawPageId(b)
}
